import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '../services/api';
import {
  Clock,
  Calendar,
  Target,
  Sparkles,
  ChevronLeft,
  ChevronRight,
  Loader2,
  RefreshCw,
  CheckCircle2,
  Circle,
  Coffee,
  Zap,
} from 'lucide-react';
import { format, addDays, subDays, isToday } from 'date-fns';
import clsx from 'clsx';

const blockTypeIcons = {
  task: CheckCircle2,
  event: Calendar,
  break: Coffee,
  buffer: Zap,
};

const blockTypeColors = {
  task: 'bg-accent-muted border-accent/30 text-accent',
  event: 'bg-info-muted border-info/30 text-info',
  break: 'bg-success-muted border-success/30 text-success',
  buffer: 'bg-surface-hover border-border text-text-tertiary',
};

export function Planner() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [activeGoal, setActiveGoal] = useState('');

  const { data: planData, isLoading, refetch } = useQuery({
    queryKey: ['plan', format(selectedDate, 'yyyy-MM-dd')],
    queryFn: () => api.getDailyPlan(format(selectedDate, 'yyyy-MM-dd')),
  });

  const createPlanMutation = useMutation({
    mutationFn: () =>
      api.createPlan({
        action: 'daily',
        date: format(selectedDate, 'yyyy-MM-dd'),
      }),
    onSuccess: () => refetch(),
  });

  const breakdownMutation = useMutation({
    mutationFn: (goal: string) =>
      api.createPlan({
        action: 'breakdown',
        goals: [goal],
      }),
  });

  const plan = planData?.data;
  const hasPlan = plan && typeof plan === 'object' && 'timeBlocks' in plan;

  const navigateDate = (direction: 'prev' | 'next') => {
    setSelectedDate(direction === 'prev' ? subDays(selectedDate, 1) : addDays(selectedDate, 1));
  };

  const handleBreakdownGoal = () => {
    if (activeGoal.trim()) {
      breakdownMutation.mutate(activeGoal);
    }
  };

  return (
    <div className="h-full p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Daily Planner</h2>
          <p className="text-sm text-text-tertiary">
            AI-powered daily planning and scheduling
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface p-1">
            <button
              onClick={() => navigateDate('prev')}
              className="rounded-md p-2 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setSelectedDate(new Date())}
              className={clsx(
                'rounded-md px-4 py-2 text-sm font-medium',
                isToday(selectedDate)
                  ? 'bg-accent-muted text-accent'
                  : 'text-text-secondary hover:bg-surface-hover'
              )}
            >
              {isToday(selectedDate) ? 'Today' : format(selectedDate, 'MMM d, yyyy')}
            </button>
            <button
              onClick={() => navigateDate('next')}
              className="rounded-md p-2 text-text-secondary hover:bg-surface-hover hover:text-text-primary"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
          <button
            onClick={() => createPlanMutation.mutate()}
            disabled={createPlanMutation.isPending}
            className="btn-primary"
          >
            {createPlanMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="h-4 w-4" />
            )}
            Generate Plan
          </button>
        </div>
      </div>

      <div className="grid h-[calc(100%-80px)] grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="col-span-2 overflow-auto rounded-xl border border-border bg-surface p-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="flex items-center gap-2 font-semibold">
              <Clock className="h-4 w-4 text-accent" />
              Daily Schedule
            </h3>
            <button
              onClick={() => refetch()}
              className="btn-ghost text-text-tertiary"
            >
              <RefreshCw className="h-4 w-4" />
            </button>
          </div>

          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-accent" />
            </div>
          ) : hasPlan && Array.isArray((plan as { timeBlocks: unknown[] }).timeBlocks) ? (
            <div className="space-y-3">
              {(plan as { timeBlocks: Array<{ start: string; end: string; activity: string; type: keyof typeof blockTypeIcons }> }).timeBlocks.map((block, index) => {
                const Icon = blockTypeIcons[block.type] || Circle;
                return (
                  <div
                    key={index}
                    className={clsx(
                      'flex items-center gap-4 rounded-lg border p-4',
                      blockTypeColors[block.type] || blockTypeColors.buffer
                    )}
                  >
                    <div className="flex flex-col items-center">
                      <span className="text-sm font-medium">
                        {block.start ? format(new Date(block.start), 'HH:mm') : '--:--'}
                      </span>
                      <span className="text-2xs text-text-tertiary">
                        {block.end ? format(new Date(block.end), 'HH:mm') : '--:--'}
                      </span>
                    </div>
                    <div className="h-10 w-px bg-current opacity-20" />
                    <Icon className="h-5 w-5" />
                    <div className="flex-1">
                      <p className="font-medium">{block.activity}</p>
                      <p className="text-xs capitalize opacity-70">{block.type}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex h-64 flex-col items-center justify-center text-center">
              <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-surface-hover">
                <Sparkles className="h-8 w-8 text-text-tertiary" />
              </div>
              <h4 className="font-semibold">No plan generated</h4>
              <p className="mt-2 max-w-sm text-sm text-text-tertiary">
                Click "Generate Plan" to create an AI-optimized schedule based on your tasks and events.
              </p>
            </div>
          )}

          {/* Tips */}
          {hasPlan && (plan as { tips?: string[] }).tips && (
            <div className="mt-6 rounded-lg bg-accent-muted p-4">
              <h4 className="mb-2 flex items-center gap-2 font-medium text-accent">
                <Target className="h-4 w-4" />
                Tips for Today
              </h4>
              <ul className="space-y-1 text-sm text-text-secondary">
                {(plan as { tips: string[] }).tips.map((tip, index) => (
                  <li key={index}>• {tip}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Goal Breakdown */}
        <div className="rounded-xl border border-border bg-surface p-6">
          <h3 className="mb-4 flex items-center gap-2 font-semibold">
            <Target className="h-4 w-4 text-success" />
            Goal Breakdown
          </h3>
          <p className="mb-4 text-sm text-text-tertiary">
            Enter a goal to break it down into actionable steps
          </p>

          <div className="space-y-4">
            <textarea
              value={activeGoal}
              onChange={(e) => setActiveGoal(e.target.value)}
              placeholder="e.g., Launch new feature by end of month"
              className="input h-24 resize-none"
            />
            <button
              onClick={handleBreakdownGoal}
              disabled={!activeGoal.trim() || breakdownMutation.isPending}
              className="btn-primary w-full"
            >
              {breakdownMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Sparkles className="h-4 w-4" />
              )}
              Break Down Goal
            </button>

            {breakdownMutation.data?.data != null && (
              <div className="mt-4 rounded-lg bg-background p-4">
                <h4 className="mb-2 font-medium">Action Steps:</h4>
                <div className="whitespace-pre-wrap text-sm text-text-secondary">
                  {(() => {
                    const data = breakdownMutation.data?.data as Record<string, unknown> | undefined;
                    if (!data) return null;
                    return typeof data === 'object' && 'breakdown' in data
                      ? String(data.breakdown)
                      : JSON.stringify(data, null, 2);
                  })()}
                </div>
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="mt-6 border-t border-border pt-6">
            <h4 className="mb-3 text-sm font-medium text-text-tertiary">Quick Actions</h4>
            <div className="space-y-2">
              <button
                onClick={() => setActiveGoal('Plan my work week effectively')}
                className="w-full rounded-lg border border-border bg-background p-3 text-left text-sm hover:border-accent/50"
              >
                📅 Plan my week
              </button>
              <button
                onClick={() => setActiveGoal('Improve my daily productivity routine')}
                className="w-full rounded-lg border border-border bg-background p-3 text-left text-sm hover:border-accent/50"
              >
                ⚡ Optimize productivity
              </button>
              <button
                onClick={() => setActiveGoal('Balance work and personal life better')}
                className="w-full rounded-lg border border-border bg-background p-3 text-left text-sm hover:border-accent/50"
              >
                ⚖️ Work-life balance
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

